import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, ref, watch } from 'vue';
import type { DropdownItems, DropdownSingleItem } from '@white-label-types/dropdown';

//components
import IconTickCircle from '@white-label-icon/icon-tick-circle';
import IconCloseCircle from '@white-label-icon/icon-close-circle';
import IconError from '@white-label-icon/icon-error';
import { TextField, DropdownWrapper } from 'ui-shared-components';

//helpers
import { format, DATE_TIME_FORMATS } from '@white-label-helper/date-utilities';

interface Traveller {
  country_of_residence?: string;
  date_of_birth?: string;
  ageGroup?: string,
  ageGroupLabel?: string,
  firstName: string;
  lastName: string;
}

interface Props {
  travellers?: Traveller[];
  predefinedTravellers?: Traveller[];
  validation?: any;
  validationMessages?: any;
  title?: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'additional-travellers-combined-product-form',
  props: {
    travellers: { default: () => [] },
    predefinedTravellers: { default: () => [] },
    validation: { default: () => ({ $each: [] }) },
    validationMessages: { default: () => ({}) },
    title: { default: () => '' }
  },
  setup(__props: any) {

const props = __props as { travellers: Traveller[], predefinedTravellers: Traveller[], validation: any, validationMessages: any, title: string };

const EMPTY_DROPDOWN_ITEM = Object.freeze({
  id: '',
  name: '',
});
const MANUAL_ENTRY_OPTION_ID = 'manual-entry';

const instance = getCurrentInstance();
const proxy = instance?.proxy as {
  $i18n: {
    locale?: string;
  };
  $t: (key: string) => string;
} | undefined;
const i18n = proxy?.$i18n;




const getDateOfBirth = (dob: string): string => {
  const dateFormat = i18n?.locale === 'en-US'
    ? DATE_TIME_FORMATS.month_day_full_year_slash_spaced
    : DATE_TIME_FORMATS.day_month_full_year_slash_spaced;
  return format(new Date(dob), dateFormat);
};

const hasPredefinedTravellers = computed(() => props.predefinedTravellers.length > 0);
const selectedPredefinedTravellerIndexes = ref<Array<number | null>>([]);
const dropdownOpened = ref<boolean[]>([]);
const manualEntryRows = ref<boolean[]>([]);

const replaceArrayValue = <T,>(items: T[], index: number, value: T): T[] => {
  return items.map((item, currentIndex) => {
    return currentIndex === index ? value : item;
  });
};

const getTravellerFullName = (traveller: Pick<Traveller, 'firstName' | 'lastName'>): string => {
  return [traveller.firstName, traveller.lastName].filter(Boolean).join(' ').trim();
};

const predefinedTravellerOptions = computed<DropdownItems>(() => {
  return props.predefinedTravellers.map((traveller, index) => ({
    id: index,
    name: getTravellerFullName(traveller),
  }));
});

const enterNameManuallyOption = computed<DropdownSingleItem>(() => ({
  id: MANUAL_ENTRY_OPTION_ID,
  name: proxy?.$t('additionalTravellersCombinedProducts.enterNameManually') ?? 'additionalTravellersCombinedProducts.enterNameManually',
}));

const selectedPredefinedTravellerIndexSet = computed(() => {
  return new Set(
    selectedPredefinedTravellerIndexes.value.filter((selectedIndex): selectedIndex is number => typeof selectedIndex === 'number'),
  );
});

const syncSelectionStateFromTravellers = (): void => {
  const usedPredefinedIndexes = new Set<number>();

  selectedPredefinedTravellerIndexes.value = props.travellers.map((traveller) => {
    const travellerFullName = getTravellerFullName(traveller);

    if (!travellerFullName) {
      return null;
    }

    const matchedOption = predefinedTravellerOptions.value.find((option) => {
      return option.name === travellerFullName && !usedPredefinedIndexes.has(option.id as number);
    });

    if (!matchedOption) {
      return null;
    }

    usedPredefinedIndexes.add(matchedOption.id as number);
    return matchedOption.id as number;
  });

  dropdownOpened.value = props.travellers.map((_, index) => {
    return dropdownOpened.value[index] ?? false;
  });

  manualEntryRows.value = props.travellers.map((traveller, index) => {
    if (!hasPredefinedTravellers.value) {
      return true;
    }

    if (selectedPredefinedTravellerIndexes.value[index] !== null) {
      return false;
    }

    if (manualEntryRows.value[index]) {
      return true;
    }

    return Boolean(traveller.firstName || traveller.lastName);
  });
};

watch(
  () => [
    props.travellers.map((traveller) => `${traveller.firstName}|${traveller.lastName}`).join('||'),
    props.predefinedTravellers.map((traveller) => `${traveller.firstName}|${traveller.lastName}`).join('||'),
  ],
  () => {
    syncSelectionStateFromTravellers();
  },
  { immediate: true },
);

watch(hasPredefinedTravellers, (hasItems) => {
  if (!hasItems) {
    manualEntryRows.value = manualEntryRows.value.map(() => true);
  }
});

const getDropdownItems = (travellerIndex: number): DropdownItems => {
  const selectedIndex = selectedPredefinedTravellerIndexes.value[travellerIndex];

  return [
    ...predefinedTravellerOptions.value.filter((option) => {
      return option.id === selectedIndex || !selectedPredefinedTravellerIndexSet.value.has(option.id as number);
    }),
    enterNameManuallyOption.value,
  ];
};

const getDropdownValue = (travellerIndex: number): DropdownSingleItem => {
  const selectedIndex = selectedPredefinedTravellerIndexes.value[travellerIndex];

  if (typeof selectedIndex !== 'number') {
    return EMPTY_DROPDOWN_ITEM;
  }

  return predefinedTravellerOptions.value.find((option) => option.id === selectedIndex) ?? EMPTY_DROPDOWN_ITEM;
};

const updateDropdownOpened = (travellerIndex: number, isOpen: boolean): void => {
  dropdownOpened.value = replaceArrayValue(dropdownOpened.value, travellerIndex, isOpen);
};

const showManualEntryFields = (travellerIndex: number): boolean => {
  return !hasPredefinedTravellers.value || manualEntryRows.value[travellerIndex];
};

const resetTravellerValidation = (travellerIndex: number): void => {
  props.validation.$each[travellerIndex]?.$reset?.();
  props.validation.$each[travellerIndex]?.firstName?.$reset?.();
  props.validation.$each[travellerIndex]?.lastName?.$reset?.();
};

const handleDropdownSelect = (travellerIndex: number, selectedItem: DropdownSingleItem): void => {
  if (selectedItem.id === MANUAL_ENTRY_OPTION_ID) {
    selectedPredefinedTravellerIndexes.value = replaceArrayValue(selectedPredefinedTravellerIndexes.value, travellerIndex, null);
    dropdownOpened.value = replaceArrayValue(dropdownOpened.value, travellerIndex, false);
    manualEntryRows.value = replaceArrayValue(manualEntryRows.value, travellerIndex, true);
    props.travellers[travellerIndex].firstName = '';
    props.travellers[travellerIndex].lastName = '';
    resetTravellerValidation(travellerIndex);
    return;
  }

  const selectedTraveller = props.predefinedTravellers[Number(selectedItem.id)];

  if (!selectedTraveller) {
    return;
  }

  selectedPredefinedTravellerIndexes.value = replaceArrayValue(selectedPredefinedTravellerIndexes.value, travellerIndex, Number(selectedItem.id));
  manualEntryRows.value = replaceArrayValue(manualEntryRows.value, travellerIndex, false);
  dropdownOpened.value = replaceArrayValue(dropdownOpened.value, travellerIndex, false);
  props.travellers[travellerIndex].firstName = selectedTraveller.firstName;
  props.travellers[travellerIndex].lastName = selectedTraveller.lastName;

  props.validation.$each[travellerIndex].firstName?.$touch();
  props.validation.$each[travellerIndex].lastName?.$touch();
};

const handleDropdownClose = (travellerIndex: number): void => {
  dropdownOpened.value = replaceArrayValue(dropdownOpened.value, travellerIndex, false);
};


return { __sfc: true,EMPTY_DROPDOWN_ITEM, MANUAL_ENTRY_OPTION_ID, instance, proxy, i18n, props, getDateOfBirth, hasPredefinedTravellers, selectedPredefinedTravellerIndexes, dropdownOpened, manualEntryRows, replaceArrayValue, getTravellerFullName, predefinedTravellerOptions, enterNameManuallyOption, selectedPredefinedTravellerIndexSet, syncSelectionStateFromTravellers, getDropdownItems, getDropdownValue, updateDropdownOpened, showManualEntryFields, resetTravellerValidation, handleDropdownSelect, handleDropdownClose, IconTickCircle, IconCloseCircle, IconError, TextField, DropdownWrapper }
}

})